import React from "react";
import styled, { keyframes } from "styled-components";

/**
 * Creatures that haunt the Wizard's Arena.
 *
 * Each is drawn as inline SVG and animated with CSS only — no rAF loops, so
 * they cost nothing while scrolled out of view, and the global
 * prefers-reduced-motion guard in GlobalStyles switches them all off.
 */

/* ══════════════ Aragog — descends on a thread, legs twitching ══════════════ */

const drop = keyframes`
  0%   { transform: translateY(-120%); }
  22%  { transform: translateY(0); }
  70%  { transform: translateY(6px); }
  100% { transform: translateY(-120%); }
`;

const twitchL = keyframes`
  0%, 100% { transform: rotate(0deg); }
  50%      { transform: rotate(-9deg); }
`;
const twitchR = keyframes`
  0%, 100% { transform: rotate(0deg); }
  50%      { transform: rotate(9deg); }
`;

export const Aragog: React.FC<{ className?: string }> = ({ className }) => (
  <AragogWrap className={className} aria-hidden="true">
    <Silk />
    <svg viewBox="0 0 80 64">
      <g className="legs-l">
        <path d="M32 30 C 20 24, 12 30, 6 20" />
        <path d="M32 34 C 18 32, 10 38, 3 34" />
        <path d="M33 38 C 20 40, 12 46, 6 48" />
        <path d="M35 42 C 24 48, 20 54, 16 60" />
      </g>
      <g className="legs-r">
        <path d="M48 30 C 60 24, 68 30, 74 20" />
        <path d="M48 34 C 62 32, 70 38, 77 34" />
        <path d="M47 38 C 60 40, 68 46, 74 48" />
        <path d="M45 42 C 56 48, 60 54, 64 60" />
      </g>
      {/* abdomen + thorax */}
      <ellipse cx="40" cy="40" rx="13" ry="11" className="body" />
      <ellipse cx="40" cy="27" rx="8.5" ry="7" className="body" />
      {/* the many eyes */}
      <g className="eyes">
        <circle cx="36" cy="24" r="1.7" /><circle cx="44" cy="24" r="1.7" />
        <circle cx="33" cy="27.5" r="1.2" /><circle cx="47" cy="27.5" r="1.2" />
        <circle cx="37.5" cy="28.5" r="1" /><circle cx="42.5" cy="28.5" r="1" />
      </g>
      {/* pincers */}
      <path d="M37 32 l-2 4 M43 32 l2 4" className="fang" />
    </svg>
  </AragogWrap>
);

/* ══════════════ Fluffy — three heads, blinking out of step ══════════════ */

const breathe = keyframes`
  0%, 100% { transform: scale(1); }
  50%      { transform: scale(1.035); }
`;

const blink = keyframes`
  0%, 92%, 100% { transform: scaleY(1); }
  95%           { transform: scaleY(0.08); }
`;

const earFlick = keyframes`
  0%, 88%, 100% { transform: rotate(0deg); }
  93%           { transform: rotate(-13deg); }
`;

export const Fluffy: React.FC<{ className?: string }> = ({ className }) => (
  <FluffyWrap className={className} aria-hidden="true">
    <svg viewBox="0 0 150 92">
      <g className="chest">
        {/* body */}
        <ellipse cx="75" cy="74" rx="46" ry="18" className="fur" />
        {/* paws */}
        <ellipse cx="46" cy="87" rx="10" ry="5" className="fur" />
        <ellipse cx="104" cy="87" rx="10" ry="5" className="fur" />
      </g>

      {[28, 75, 122].map((cx, i) => (
        <g key={cx} className={`head head-${i}`} style={{ transformOrigin: `${cx}px 44px` }}>
          {/* ears */}
          <path d={`M${cx - 15} 30 l-5 -16 l13 7 Z`} className={`fur ear ear-${i}`}
            style={{ transformOrigin: `${cx - 12}px 26px` }} />
          <path d={`M${cx + 15} 30 l5 -16 l-13 7 Z`} className={`fur ear ear-${i}`}
            style={{ transformOrigin: `${cx + 12}px 26px` }} />
          {/* skull + muzzle */}
          <ellipse cx={cx} cy="42" rx="17" ry="15" className="fur" />
          <ellipse cx={cx} cy="52" rx="9" ry="7" className="muzzle" />
          <ellipse cx={cx} cy="48" rx="3" ry="2.2" className="nose" />
          {/* eyes */}
          <g className={`eyes eyes-${i}`} style={{ transformOrigin: `${cx}px 39px` }}>
            <ellipse cx={cx - 6.5} cy="39" rx="3.2" ry="3.6" />
            <ellipse cx={cx + 6.5} cy="39" rx="3.2" ry="3.6" />
          </g>
          <circle cx={cx - 5.6} cy="38" r="1" className="glint" />
          <circle cx={cx + 7.4} cy="38" r="1" className="glint" />
          {/* teeth */}
          <path d={`M${cx - 4} 56 l2 4 l2 -4 M${cx + 0} 56 l2 4 l2 -4`} className="teeth" />
        </g>
      ))}
    </svg>
  </FluffyWrap>
);

/* ══════════════ Nagini — slithers past, tongue flicking ══════════════ */

const slither = keyframes`
  0%   { transform: translateX(-18%) scaleX(1); opacity: 0; }
  8%   { opacity: 1; }
  92%  { opacity: 1; }
  100% { transform: translateX(118%) scaleX(1); opacity: 0; }
`;

const undulate = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  25%      { transform: translateY(-3px) rotate(-1.5deg); }
  75%      { transform: translateY(3px) rotate(1.5deg); }
`;

const tongue = keyframes`
  0%, 78%, 100% { transform: scaleX(0); opacity: 0; }
  84%, 92%      { transform: scaleX(1); opacity: 1; }
`;

export const Nagini: React.FC<{ className?: string; duration?: number }> = ({ className, duration = 26 }) => (
  <NaginiWrap className={className} $d={duration} aria-hidden="true">
    <svg viewBox="0 0 200 40" className="snake">
      {/* body — one long tapering curve */}
      <path d="M2 22 C 30 8, 52 34, 80 22 C 108 10, 130 32, 158 22"
        className="scales" strokeWidth="9" />
      <path d="M2 22 C 30 8, 52 34, 80 22 C 108 10, 130 32, 158 22"
        className="sheen" strokeWidth="3" />
      {/* head */}
      <ellipse cx="166" cy="21" rx="12" ry="7.5" className="hood" />
      <circle cx="171" cy="19" r="1.6" className="eye" />
      <path d="M177 21 l9 -3 M177 21 l9 3" className="tongue" />
    </svg>
  </NaginiWrap>
);

/* ══════════════ styles ══════════════ */

const AragogWrap = styled.div`
  position: absolute;
  top: 0;
  right: 5%;
  width: 62px;
  z-index: 3;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: ${drop} 17s ease-in-out infinite;

  svg { width: 100%; height: auto; overflow: visible; }
  .body   { fill: #17121d; stroke: rgba(180,150,210,0.22); stroke-width: 1; }
  .eyes circle { fill: #c0392b; opacity: 0.85; }
  .fang   { stroke: rgba(200,170,220,0.45); stroke-width: 1.4; fill: none; stroke-linecap: round; }
  .legs-l path, .legs-r path {
    fill: none; stroke: #17121d; stroke-width: 2.6; stroke-linecap: round;
  }
  .legs-l { transform-origin: 34px 36px; animation: ${twitchL} 2.6s ease-in-out infinite; }
  .legs-r { transform-origin: 46px 36px; animation: ${twitchR} 2.6s ease-in-out infinite; }

  @media (max-width: 820px) { display: none; }
`;

const Silk = styled.span`
  width: 1px;
  height: 46px;
  background: linear-gradient(180deg, rgba(220,215,240,0.02), rgba(220,215,240,0.3));
`;

const FluffyWrap = styled.div`
  width: 168px;
  pointer-events: none;

  svg { width: 100%; height: auto; overflow: visible; }
  .fur    { fill: #241c2e; stroke: rgba(150,120,190,0.16); stroke-width: 1; }
  .muzzle { fill: #16111d; }
  .nose   { fill: #0c0810; }
  .teeth  { fill: none; stroke: rgba(240,235,250,0.55); stroke-width: 1.1; }
  .eyes ellipse { fill: #ffcf5a; }
  .glint  { fill: #fff8e0; opacity: 0.9; }

  .chest { transform-origin: 75px 74px; animation: ${breathe} 4.2s ease-in-out infinite; }
  .eyes-0 { animation: ${blink} 5.5s infinite; }
  .eyes-1 { animation: ${blink} 7.3s infinite 1.1s; }
  .eyes-2 { animation: ${blink} 6.1s infinite 2.4s; }
  .ear-0  { animation: ${earFlick} 6.8s infinite; }
  .ear-1  { animation: ${earFlick} 9.1s infinite 2s; }
  .ear-2  { animation: ${earFlick} 7.6s infinite 3.6s; }

  @media (max-width: 700px) { width: 122px; }
`;

const NaginiWrap = styled.div<{ $d: number }>`
  position: absolute;
  left: 0;
  bottom: 4px;
  width: 190px;
  z-index: 2;
  pointer-events: none;
  animation: ${slither} ${({ $d }) => $d}s linear infinite;

  svg { width: 100%; height: auto; overflow: visible; }
  .snake  { animation: ${undulate} 2.4s ease-in-out infinite; transform-origin: 50% 50%; }
  .scales { fill: none; stroke: #1d4f33; stroke-linecap: round; }
  .sheen  { fill: none; stroke: rgba(120,220,160,0.22); stroke-linecap: round; }
  .hood   { fill: #1d4f33; stroke: rgba(120,220,160,0.28); stroke-width: 1; }
  .eye    { fill: #ffcf5a; }
  .tongue {
    stroke: #c0392b; stroke-width: 1.3; fill: none; stroke-linecap: round;
    transform-origin: 177px 21px;
    animation: ${tongue} 3.4s ease-in-out infinite;
  }

  @media (max-width: 700px) { width: 130px; }
`;
